import { Center, Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay, useDisclosure } from '@chakra-ui/core';
import React, { useEffect, useRef } from 'react';
import VideoPlayer from './VideoPlayer';

interface VideoPreviewModalBaseProps<T> {
  video: T | null;
  getVideoKey: (video: T | null) => string;
  getVideoSrc: (video: T | null) => string;
  getVideoAlt?: (video: T | null) => string;
  isDisabled?: boolean;
  modalSize?: string;
  previewVideoHeight?: string;
}

interface VideoPreviewModalWithNodeTrigger<T> extends VideoPreviewModalBaseProps<T> {
  setTrigger: JSX.Element;
  onModalClose?: () => void;
}

interface VideoPreviewModalWithBooleanTrigger<T> extends VideoPreviewModalBaseProps<T> {
  setTrigger: boolean;
  onModalClose: () => void;
}

export type VideoPreviewModalProps<T> = VideoPreviewModalWithNodeTrigger<T> | VideoPreviewModalWithBooleanTrigger<T>;

/**
 * A modal for previewing videos.
 *
 * The modal shows a video gallery, where the user can select a video by clicking on the thumbnail.
 * The selected video is highlighted with a border.
 * The modal also shows a close button on the top right corner.
 * The modal can be triggered by a boolean, or a node element.
 * Download is disabled via `controlsList="nodownload"` and `onContextMenu` prevention.
 *
 * @param {VideoPreviewModalProps<T>} props The props for the modal.
 * @param {T[]} props.videos The videos to be displayed.
 * @param {(video: T | null) => string} props.getVideoSrc A function that returns the src of the video.
 * @param {() => void} [props.onModalClose] A callback function invoked when the modal is closed.
 * @param {T | null} [props.selectedVideo] The currently selected video.
 * @param {T | null} [props.currentActive] The currently active video.
 * @returns {React.ReactElement} The React component.
 */

export function VideoPreviewModal<T>({
  setTrigger,
  video,
  getVideoSrc,
  onModalClose,
  isDisabled = false,
  modalSize = '6xl',
  previewVideoHeight = '500px',
}: VideoPreviewModalProps<T>) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [error, setError] = React.useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const isTriggerElement = React.isValidElement(setTrigger);

  useEffect(() => {
    if (!isOpen && videoRef.current) {
      videoRef.current.pause();
    }
  }, [isOpen]);

  function handleTriggerClick() {
    if (!error && !isDisabled) onOpen();
  }

  useEffect(() => {
    if (!isTriggerElement && setTrigger) {
      handleTriggerClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTriggerElement, setTrigger]);

  if (!video) return null;

  function handleModalClose() {
    onClose();
    if (onModalClose && !isTriggerElement) onModalClose();
  }

  const triggerElement =
    isTriggerElement &&
    React.cloneElement(setTrigger as React.ReactElement<any>, {
      onError: () => setError(true),
      onLoad: () => setError(false),
    });

  return (
    <>
      {isTriggerElement && (
        <Center onClick={handleTriggerClick} data-testid="open-modal-button" width="fit-content">
          {triggerElement}
        </Center>
      )}

      <Modal isCentered isOpen={isOpen} onClose={handleModalClose} size={modalSize} autoFocus={false} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent width="55vw" maxH="calc(90vh - 80px)" bg="#455A64" position="relative" data-testid="video-preview-modal">
          <ModalCloseButton
            position="absolute"
            top={4}
            right={4}
            zIndex={10}
            width="32px"
            height="32px"
            color="#fff"
            data-testid="close-modal-button"
          />

          <ModalBody p={0}>
            <Center>
              <VideoPlayer
                ref={videoRef}
                key={getVideoSrc(video)}
                src={getVideoSrc(video)}
                controls
                controlsList="nodownload noremoteplayback"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                style={{
                  width: '100%',
                  height: previewVideoHeight,
                  maxWidth: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
                data-testid="preview-video"
              />
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
